import React, { useState } from 'react';
import { X, Loader2, Save } from 'lucide-react';
import { Button } from '../Button';
import { rechtFehlerOder } from '../../services/rechte/rechtFehler';
import { readOnlyMessage } from '../../services/readOnlyNotice';
import { KATEGORIEN, MODULE, formatBytes, parseTags, toggleModul } from './dokumenteLogic';
import { DokumentKategorie, DokumentModul, ProjektDokument } from './types';

interface Props {
  dokument: ProjektDokument;
  ordnerVorschlaege: string[];
  /** Zweite Reihe gegen den Lesemodus — Begruendung wie im Upload-Dialog. */
  isReadOnly?: boolean;
  onAbbrechen: () => void;
  onSpeichern: (updates: Partial<ProjektDokument>) => Promise<void>;
}

/**
 * Die Angaben AM Dokument nachtragen — Titel, Einordnung, Stand, Ablauf,
 * Modulbezug.
 *
 * Die DATEI selbst kommt hier nicht vor, und das ist Absicht: sie wird nie
 * ueberschrieben (der Bucket hat gar keine update-Policy). Eine neue Fassung
 * eines Plans ist ein neues Dokument mit eigenem Stand — nur so bleibt
 * nachvollziehbar, welcher Stand wann galt.
 */
export const DokumentBearbeitenDialog: React.FC<Props> = ({ dokument, ordnerVorschlaege, isReadOnly = false, onAbbrechen, onSpeichern }) => {
  const [titel, setTitel] = useState(dokument.titel);
  const [kategorie, setKategorie] = useState<DokumentKategorie>(dokument.kategorie);
  const [ordner, setOrdner] = useState(dokument.ordner || '');
  const [beschreibung, setBeschreibung] = useState(dokument.beschreibung || '');
  const [stand, setStand] = useState(dokument.stand || '');
  const [gueltigBis, setGueltigBis] = useState((dokument.gueltig_bis || '').slice(0, 10));
  const [tagsText, setTagsText] = useState(dokument.tags.join(', '));
  const [module, setModule] = useState<DokumentModul[]>(dokument.module);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const speichern = async () => {
    if (!titel.trim() || laeuft) return;
    if (isReadOnly) {
      setFehler(readOnlyMessage('Die Änderungen wurden nicht gespeichert.'));
      return;
    }
    setLaeuft(true);
    setFehler(null);
    try {
      await onSpeichern({
        titel,
        kategorie,
        ordner,
        // Leerstring statt undefined: ein ausgelassener Schluessel loescht in
        // PostgREST nichts — der Service macht daraus NULL.
        beschreibung,
        stand,
        gueltig_bis: gueltigBis,
        tags: parseTags(tagsText),
        module,
      });
      onAbbrechen();
    } catch (e) {
      setFehler(rechtFehlerOder(e));
      setLaeuft(false);
    }
  };

  const feld = 'w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm '
    + 'focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all';
  const beschriftung = 'block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5';

  return (
    <div className="fixed inset-0 z-[150] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-white rounded-2xl w-full max-w-xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between shrink-0">
          <div className="min-w-0">
            <h3 className="font-bold text-slate-800 truncate">Dokument bearbeiten</h3>
            <p className="text-xs text-slate-500 mt-0.5 truncate">{dokument.dateiname} · {formatBytes(dokument.groesse_bytes)}</p>
          </div>
          <button onClick={onAbbrechen} disabled={laeuft} className="p-2 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40 shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto space-y-4">
          <div>
            <label className={beschriftung}>Titel</label>
            <input value={titel} onChange={e => setTitel(e.target.value)} disabled={laeuft} className={feld} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={beschriftung}>Kategorie</label>
              <select value={kategorie} onChange={e => setKategorie(e.target.value as DokumentKategorie)} disabled={laeuft} className={feld}>
                {KATEGORIEN.map(k => <option key={k.id} value={k.id}>{k.label}</option>)}
              </select>
            </div>
            <div>
              <label className={beschriftung}>Ordner</label>
              <input value={ordner} onChange={e => setOrdner(e.target.value)} disabled={laeuft} list="dokumente-ordner-edit" className={feld} />
              <datalist id="dokumente-ordner-edit">
                {ordnerVorschlaege.map(o => <option key={o} value={o} />)}
              </datalist>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={beschriftung}>Stand / Revision</label>
              <input value={stand} onChange={e => setStand(e.target.value)} disabled={laeuft} placeholder="z. B. Rev. C" className={feld} />
            </div>
            <div>
              <label className={beschriftung}>Gültig bis</label>
              <input type="date" value={gueltigBis} onChange={e => setGueltigBis(e.target.value)} disabled={laeuft} className={feld} />
              <p className="text-[11px] text-slate-400 mt-1">Nur für Nachweise, die ablaufen. Leer = läuft nicht ab.</p>
            </div>
          </div>

          <div>
            <label className={beschriftung}>Beschreibung</label>
            <textarea value={beschreibung} onChange={e => setBeschreibung(e.target.value)} disabled={laeuft} rows={2} className={feld} />
          </div>

          <div>
            <label className={beschriftung}>Schlagworte</label>
            <input value={tagsText} onChange={e => setTagsText(e.target.value)} disabled={laeuft} placeholder="mit Komma getrennt" className={feld} />
          </div>

          <div>
            <label className={beschriftung}>Auch anzeigen in</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {MODULE.map(m => {
                const an = module.includes(m.id);
                return (
                  <button
                    key={m.id} type="button" disabled={laeuft}
                    onClick={() => setModule(prev => toggleModul(prev, m.id))}
                    title={m.verfuegbar ? undefined : 'Das Modul ist noch in Entwicklung — die Zuordnung wird jetzt schon gespeichert.'}
                    className={`px-3 min-h-[44px] rounded-lg text-xs font-semibold border transition-colors disabled:opacity-40 ${
                      an ? 'bg-blue-600 text-white border-blue-600 shadow-sm' : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
                    }`}
                  >
                    {m.label}{m.verfuegbar ? '' : ' (bald)'}
                  </button>
                );
              })}
            </div>
          </div>

          {fehler && (
            <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-sm text-rose-700 whitespace-pre-line">{fehler}</div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-slate-200 flex items-center justify-end gap-2 shrink-0 bg-slate-50">
          <Button variant="ghost" onClick={onAbbrechen} disabled={laeuft}>Abbrechen</Button>
          <Button
            onClick={speichern} disabled={!titel.trim() || laeuft || isReadOnly}
            icon={laeuft ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          >
            Speichern
          </Button>
        </div>
      </div>
    </div>
  );
};
